import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useBranch } from '@/features/branch/branch-context';
import { api, BRANCH_HEADER } from '@/lib/api';
import type {
  ApproveRxRequestInput,
  RefuseRxRequestInput,
  RouteRxRequestInput,
  RxRequestFilters,
  StaffRxRequest,
} from './types';

const LIVE_MS = 30_000;

function useBranchKey() {
  const { selected, canSelectAll } = useBranch();
  return selected?.id ?? (canSelectAll ? 'all' : 'none');
}

const all = ['prescription-requests'] as const;
const detailKey = (id: string) => [...all, 'detail', id] as const;

/** GET /prescription-requests?status&kind: my inbox (doctor) or the branch (manager). */
export function usePrescriptionRequests(filters: RxRequestFilters) {
  const branch = useBranchKey();
  return useQuery({
    queryKey: [...all, 'list', branch, filters.status, filters.kind],
    queryFn: async ({ signal }) =>
      (
        await api.get<StaffRxRequest[]>('/prescription-requests', {
          signal,
          query: { status: filters.status, kind: filters.kind || undefined },
        })
      ).data,
    enabled: branch !== 'none',
    refetchInterval: LIVE_MS,
  });
}

/** GET /prescription-requests/{id} (medicines for the doctor only). */
export function usePrescriptionRequest(id: string) {
  return useQuery({
    queryKey: detailKey(id),
    queryFn: async ({ signal }) =>
      (
        await api.get<StaffRxRequest>(`/prescription-requests/${id}`, {
          signal,
        })
      ).data,
    retry: false,
  });
}

/** Doctors of the request's branch, for routing (GET /doctors, `calendar:read`). */
export function useBranchDoctors(branchId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['doctors', branchId],
    queryFn: async ({ signal }) =>
      (
        await api.get<{ id: string; fullName: string }[]>('/doctors', {
          signal,
          headers: { [BRANCH_HEADER]: branchId },
        })
      ).data,
    enabled,
    staleTime: 5 * 60_000,
  });
}

function useDecision<I>(id: string, action: 'approve' | 'refuse' | 'route') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: I) =>
      (
        await api.post<StaffRxRequest>(
          `/prescription-requests/${id}/${action}`,
          body,
        )
      ).data,
    onSuccess: (saved) => {
      qc.setQueryData(detailKey(id), saved);
      void qc.invalidateQueries({ queryKey: [...all, 'list'] });
      void qc.invalidateQueries({ queryKey: ['shell', 'counts'] });
    },
  });
}

/** POST …/approve {version, allergyOverride?}. */
export const useApproveRxRequest = (id: string) =>
  useDecision<ApproveRxRequestInput>(id, 'approve');

/** POST …/refuse {version, reason}. */
export const useRefuseRxRequest = (id: string) =>
  useDecision<RefuseRxRequestInput>(id, 'refuse');

/** POST …/route {doctorId | null, version} (manager). */
export const useRouteRxRequest = (id: string) =>
  useDecision<RouteRxRequestInput>(id, 'route');
